import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Lock,
  Save,
  Trash2,
} from "lucide-react";

import { withMemberGate } from "@/lib/with-member-gate";
import { PauseGround } from "@/components/process/PauseGround";
import { ChipGroup } from "@/components/dissociation/ChipGroup";
import { IntensitySlider } from "@/components/dissociation/IntensitySlider";
import { QuickCheckIn } from "@/components/dissociation/QuickCheckIn";
import { PatternsPanel } from "@/components/dissociation/PatternsPanel";
import { JournalGuide } from "@/components/JournalGuide";
import {
  AFTER_OPTIONS,
  BEFORE_TAGS,
  BODY_OPTIONS,
  HELPED_OPTIONS,
  NOTICED_OPTIONS,
  buildPatterns,
} from "@/lib/dissociation-journal";
import {
  deleteDissociationEntry,
  listDissociationEntries,
  saveDissociationEntry,
  type DissociationEntry,
} from "@/lib/dissociation.functions";

export const Route = createFileRoute("/dissociation-journal")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Dissociation & Grounding Journal — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A gentle, private journal for noticing disconnection, tracking your own early signs, and discovering what helps you reconnect with the present.",
      },
      {
        property: "og:title",
        content: "Dissociation & Grounding Journal — Recovery Trauma™",
      },
      {
        property: "og:description",
        content: "Notice. Ground. Understand your patterns. Reconnect.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://recoverytrauma.com/dissociation-journal" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/dissociation-journal" },
    ],
  }),
  component: withMemberGate(DissociationJournalPage, "Dissociation & Grounding Journal"),
});

type Draft = {
  id?: string;
  beforeNote: string;
  beforeTags: string[];
  noticed: string[];
  noticedNote: string;
  bodySignals: string[];
  bodyNote: string;
  intensity: number;
  helped: string[];
  helpedNote: string;
  afterFeelings: string[];
  needNote: string;
};

const EMPTY: Draft = {
  beforeNote: "",
  beforeTags: [],
  noticed: [],
  noticedNote: "",
  bodySignals: [],
  bodyNote: "",
  intensity: 5,
  helped: [],
  helpedNote: "",
  afterFeelings: [],
  needNote: "",
};

const STEPS = [
  "Before",
  "What you noticed",
  "Body check",
  "How disconnected",
  "What helped",
  "After",
] as const;

const QUERY_KEY = ["dissociation-entries"];

function toDraft(entry: DissociationEntry): Draft {
  return {
    id: entry.id,
    beforeNote: entry.before_note ?? "",
    beforeTags: entry.before_tags ?? [],
    noticed: entry.noticed ?? [],
    noticedNote: entry.noticed_note ?? "",
    bodySignals: entry.body_signals ?? [],
    bodyNote: entry.body_note ?? "",
    intensity: entry.intensity ?? 5,
    helped: entry.helped ?? [],
    helpedNote: entry.helped_note ?? "",
    afterFeelings: entry.after_feelings ?? [],
    needNote: entry.need_note ?? "",
  };
}

function DissociationJournalPage() {
  const list = useServerFn(listDissociationEntries);
  const save = useServerFn(saveDissociationEntry);
  const remove = useServerFn(deleteDissociationEntry);
  const qc = useQueryClient();

  const query = useQuery({ queryKey: QUERY_KEY, queryFn: () => list() });
  const entries = query.data?.entries ?? [];
  const patterns = useMemo(() => buildPatterns(entries), [entries]);
  const drafts = entries.filter((e) => e.kind === "entry" && e.status === "draft");
  const completed = entries.filter((e) => e.kind === "entry" && e.status === "complete");

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [step, setStep] = useState(0);

  const invalidate = () => void qc.invalidateQueries({ queryKey: QUERY_KEY });

  const saveMutation = useMutation({
    mutationFn: (input: Parameters<typeof save>[0]) => save(input),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: invalidate,
  });

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const toggle = (key: "beforeTags" | "noticed" | "bodySignals" | "helped" | "afterFeelings") =>
    (value: string) =>
      setDraft((prev) => {
        const current = prev[key];
        return {
          ...prev,
          [key]: current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value],
        };
      });

  const persist = async (status: "draft" | "complete") => {
    const saved = await saveMutation.mutateAsync({
      data: {
        id: draft.id,
        kind: "entry",
        status,
        beforeNote: draft.beforeNote || null,
        beforeTags: draft.beforeTags,
        noticed: draft.noticed,
        noticedNote: draft.noticedNote || null,
        bodySignals: draft.bodySignals,
        bodyNote: draft.bodyNote || null,
        intensity: draft.intensity,
        helped: draft.helped,
        helpedNote: draft.helpedNote || null,
        afterFeelings: draft.afterFeelings,
        needNote: draft.needNote || null,
      },
    });
    if (status === "complete") {
      toast.success("Entry saved. Thank you for noticing.");
      setDraft(EMPTY);
      setStep(0);
    } else {
      toast.success("Saved — you can come back to this whenever you're ready.");
      setDraft((prev) => ({ ...prev, id: saved.id }));
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-2xl px-4 pt-6 sm:px-6">

        <header
          className="mt-4 rounded-[26px] border border-border/60 p-5 shadow-sm sm:p-7"
          style={{
            backgroundImage:
              "linear-gradient(150deg, var(--qa-mint-tint) 0%, var(--qa-teal-tint) 45%, var(--card) 100%)",
          }}
        >
          <p
            className="inline-flex rounded-full bg-card px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--qa-teal-ink)" }}
          >
            Process · Journal
          </p>
          <h1 className="mt-3 font-display text-[1.75rem] leading-tight text-foreground sm:text-4xl">
            Dissociation &amp; Grounding Journal
          </h1>
          <p className="mt-3 text-[1rem] leading-relaxed text-muted-foreground">
            Notice → ground → understand your patterns → reconnect. One question at a
            time, and you can stop anywhere.
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-3 py-1.5 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" aria-hidden /> Private to you
          </p>
        </header>
        <JournalGuide journal="dissociation" className="mt-6" defaultOpen />

        <PauseGround className="mt-6" />

        <QuickCheckIn
          isSaving={saveMutation.isPending}
          onSave={async ({ presence, needChoice }) => {
            await saveMutation.mutateAsync({
              data: { kind: "checkin", status: "complete", presence, needChoice },
            });
          }}
        />

        {/* Guided entry */}
        <section className="mt-6 rounded-3xl border border-border/70 bg-card/80 p-5 shadow-sm sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Step {step + 1} of {STEPS.length} · {STEPS[step]}
            </p>
            {draft.id && (
              <span className="text-xs text-muted-foreground">Saved draft</span>
            )}
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-border/60">
            <div
              className="h-full rounded-full bg-[var(--gold-deep,#c9a227)] transition-all"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            />
          </div>

          <div className="mt-6 space-y-5">
            {step === 0 && (
              <>
                <h2 className="font-display text-2xl leading-tight text-foreground">
                  What was happening before you noticed yourself disconnecting?
                </h2>
                <p className="text-sm text-muted-foreground">
                  Where you were, what you were doing, how you were feeling. Only as
                  much as you want to write.
                </p>
                <textarea
                  rows={5}
                  value={draft.beforeNote}
                  onChange={(e) => set("beforeNote", e.target.value)}
                  placeholder="There's no right amount to say here…"
                  className="w-full resize-none rounded-2xl border border-border/60 bg-background/70 p-4 font-serif text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
                />
                <p className="text-sm font-semibold text-foreground">
                  Anything that was already true for you?
                </p>
                <ChipGroup
                  options={BEFORE_TAGS}
                  selected={draft.beforeTags}
                  onToggle={toggle("beforeTags")}
                />
              </>
            )}

            {step === 1 && (
              <>
                <h2 className="font-display text-2xl leading-tight text-foreground">
                  What did you notice?
                </h2>
                <p className="text-sm text-muted-foreground">
                  Choose as many or as few as fit. These are simply observations —
                  not a test or an assessment of anything.
                </p>
                <ChipGroup
                  options={NOTICED_OPTIONS}
                  selected={draft.noticed}
                  onToggle={toggle("noticed")}
                />
                <textarea
                  rows={3}
                  value={draft.noticedNote}
                  onChange={(e) => set("noticedNote", e.target.value)}
                  placeholder="In your own words (optional)…"
                  className="w-full resize-none rounded-2xl border border-border/60 bg-background/70 p-4 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
                />
              </>
            )}

            {step === 2 && (
              <>
                <h2 className="font-display text-2xl leading-tight text-foreground">
                  What did you notice in your body?
                </h2>
                <ChipGroup
                  options={BODY_OPTIONS}
                  selected={draft.bodySignals}
                  onToggle={toggle("bodySignals")}
                />
                <textarea
                  rows={3}
                  value={draft.bodyNote}
                  onChange={(e) => set("bodyNote", e.target.value)}
                  placeholder="Anything else your body was doing (optional)…"
                  className="w-full resize-none rounded-2xl border border-border/60 bg-background/70 p-4 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
                />
              </>
            )}

            {step === 3 && (
              <>
                <h2 className="font-display text-2xl leading-tight text-foreground">
                  How disconnected did you feel?
                </h2>
                <IntensitySlider
                  id="intensity"
                  value={draft.intensity}
                  onChange={(v) => set("intensity", v)}
                  minLabel="Slightly"
                  maxLabel="Extremely"
                />
              </>
            )}

            {step === 4 && (
              <>
                <h2 className="font-display text-2xl leading-tight text-foreground">
                  What helped you reconnect?
                </h2>
                <p className="text-sm text-muted-foreground">
                  Tap to record it. The small link on a chip opens that practice if
                  you'd like to use it now.
                </p>
                <div className="flex flex-wrap gap-2">
                  {HELPED_OPTIONS.map((option) => {
                    const active = draft.helped.includes(option.label);
                    return (
                      <span
                        key={option.label}
                        className={`inline-flex min-h-[44px] items-center rounded-full border text-sm transition ${
                          active
                            ? "border-transparent bg-foreground text-background"
                            : "border-border bg-background/60 text-foreground"
                        }`}
                      >
                        <button
                          type="button"
                          aria-pressed={active}
                          onClick={() => toggle("helped")(option.label)}
                          className="inline-flex items-center gap-1.5 px-4 py-2"
                        >
                          {active && <Check className="h-3.5 w-3.5" aria-hidden />}
                          {option.label}
                        </button>
                        {option.to && (
                          <Link
                            to={option.to}
                            className={`border-s px-3 py-2 text-xs underline-offset-2 hover:underline ${
                              active
                                ? "border-background/30"
                                : "border-border text-muted-foreground"
                            }`}
                          >
                            Open
                          </Link>
                        )}
                      </span>
                    );
                  })}
                </div>
                <textarea
                  rows={3}
                  value={draft.helpedNote}
                  onChange={(e) => set("helpedNote", e.target.value)}
                  placeholder="Something else that helped (optional)…"
                  className="w-full resize-none rounded-2xl border border-border/60 bg-background/70 p-4 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
                />
              </>
            )}

            {step === 5 && (
              <>
                <h2 className="font-display text-2xl leading-tight text-foreground">
                  How do you feel now?
                </h2>
                <ChipGroup
                  options={AFTER_OPTIONS}
                  selected={draft.afterFeelings}
                  onToggle={toggle("afterFeelings")}
                />
                <p className="text-sm font-semibold text-foreground">
                  What do you need right now?
                </p>
                <textarea
                  rows={4}
                  value={draft.needNote}
                  onChange={(e) => set("needNote", e.target.value)}
                  placeholder="Rest, warmth, company, quiet, nothing at all…"
                  className="w-full resize-none rounded-2xl border border-border/60 bg-background/70 p-4 font-serif text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
                />
              </>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-border px-4 text-sm text-foreground transition hover:border-gold/50"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden /> Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-foreground px-5 text-sm font-semibold text-background transition hover:opacity-90"
              >
                Next <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void persist("complete")}
                disabled={saveMutation.isPending}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-foreground px-5 text-sm font-semibold text-background transition hover:opacity-90 disabled:opacity-60"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Check className="h-4 w-4" aria-hidden />
                )}
                Finish entry
              </button>
            )}
            <button
              type="button"
              onClick={() => void persist("draft")}
              disabled={saveMutation.isPending}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-border px-4 text-sm text-foreground transition hover:border-gold/50 disabled:opacity-60"
            >
              <Save className="h-4 w-4" aria-hidden /> Save &amp; finish later
            </button>
          </div>
        </section>

        {drafts.length > 0 && (
          <section className="mt-6 rounded-3xl border border-border/70 bg-background/70 p-5">
            <h2 className="font-display text-lg text-foreground">Unfinished entries</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Pick one back up whenever you have the capacity.
            </p>
            <ul className="mt-3 space-y-2">
              {drafts.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card/70 px-4 py-3"
                >
                  <span className="text-sm text-foreground">
                    {new Date(entry.created_at).toLocaleDateString()} ·{" "}
                    <span className="text-muted-foreground">
                      {(entry.noticed ?? []).slice(0, 2).join(", ") || "Started"}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setDraft(toDraft(entry));
                        setStep(0);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="rounded-full border border-border px-3 py-1.5 text-xs text-foreground hover:border-gold/50"
                    >
                      Continue
                    </button>
                    <button
                      type="button"
                      aria-label="Delete draft"
                      onClick={() => void deleteMutation.mutateAsync(entry.id)}
                      className="rounded-full border border-border p-1.5 text-muted-foreground hover:text-foreground"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-6">
          <PatternsPanel patterns={patterns} />
        </div>

        {completed.length > 0 && (
          <section className="mt-6 rounded-3xl border border-border/70 bg-background/70 p-5">
            <h2 className="font-display text-lg text-foreground">Past entries</h2>
            <ul className="mt-3 space-y-2">
              {completed.slice(0, 12).map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-start justify-between gap-3 rounded-2xl border border-border/60 bg-card/70 px-4 py-3"
                >
                  <div className="text-sm">
                    <p className="text-foreground">
                      {new Date(entry.created_at).toLocaleDateString()}
                      {entry.intensity ? ` · felt ${entry.intensity}/10` : ""}
                    </p>
                    <p className="mt-0.5 text-muted-foreground">
                      {(entry.helped ?? []).slice(0, 3).join(", ") ||
                        (entry.noticed ?? []).slice(0, 3).join(", ")}
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label="Delete entry"
                    onClick={() => void deleteMutation.mutateAsync(entry.id)}
                    className="rounded-full border border-border p-1.5 text-muted-foreground hover:text-foreground"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <PauseGround className="mt-6" />
      </div>
    </div>
  );
}
